import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { pageInView } from '../model/pageInView';

const TOP_GAP = 24; // the space left above a page jumped to
export const PAGE_ATTR = 'data-pdf-page';

type Options = {
  scrollRef: RefObject<HTMLDivElement | null>;
  /** The page asked for from outside: a citation, or the address. */
  page: number;
  /** How many pages are laid out; 0 until the PDF has loaded. */
  pages: number;
  zoom: number;
  onPage: (page: number) => void;
};

function readPage(el: HTMLElement): number | null {
  const view = el.getBoundingClientRect();
  const boxes = Array.from(el.querySelectorAll<HTMLElement>(`[${PAGE_ATTR}]`)).map((node) => {
    const box = node.getBoundingClientRect();
    return { page: Number(node.getAttribute(PAGE_ATTR)), top: box.top - view.top, bottom: box.bottom - view.top };
  });
  return pageInView(boxes, 0, el.clientHeight, el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
}

/** Keeps a continuous PDF and its page number in step, both ways: scrolling reports the page, and a page asked for from
 * outside scrolls to it. Jumps are instant, as a smooth one would report every page it passed and fight itself. */
export function usePageScroll({ scrollRef, page, pages, zoom, onPage }: Options) {
  const [current, setCurrent] = useState(page);
  const shown = useRef(page);
  const placed = useRef(false);
  const jumpedTo = useRef<number | null>(null); // the scroll event a jump itself causes is not the reader moving
  const reported = useRef<number[]>([]); // pages told to the parent, which come back as the page prop
  const latestOnPage = useRef(onPage);
  useEffect(() => { latestOnPage.current = onPage; }, [onPage]);

  const goTo = useCallback((target: number) => {
    const el = scrollRef.current;
    const node = el?.querySelector(`[${PAGE_ATTR}="${String(target)}"]`);
    if (!el || !node) return;
    el.scrollTop += node.getBoundingClientRect().top - el.getBoundingClientRect().top - TOP_GAP;
    jumpedTo.current = el.scrollTop;
    shown.current = target;
    setCurrent(target);
  }, [scrollRef]);

  // A page asked for from outside. Our own report coming back is not a request, even if the reader has moved on since.
  useLayoutEffect(() => {
    if (pages === 0) return;
    const echo = reported.current.indexOf(page);
    if (echo >= 0) {
      reported.current.splice(0, echo + 1);
      return;
    }
    const target = Math.min(Math.max(page, 1), pages);
    if (!placed.current || target !== shown.current) goTo(target);
    placed.current = true;
  }, [page, pages, goTo]);

  // A new zoom changes every page's height: stay on the same page.
  useLayoutEffect(() => {
    if (placed.current) goTo(shown.current);
  }, [zoom, goTo]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || pages === 0) return;
    let frame = 0;
    const onScroll = () => {
      if (jumpedTo.current !== null && Math.abs(el.scrollTop - jumpedTo.current) < 1) return;
      jumpedTo.current = null;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const seen = readPage(el);
        if (seen === null || seen === shown.current) return;
        shown.current = seen;
        setCurrent(seen);
        reported.current.push(seen);
        latestOnPage.current(seen);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('scroll', onScroll);
    };
  }, [scrollRef, pages]);

  return { current, goTo };
}
